import React, { useEffect, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Kpi } from "@/components/ui-bits";
import { ShoppingCart, IndianRupee, AlertCircle, PiggyBank, Users, Package, TrendingUp } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend } from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(r => setStats(r.data));
  }, []);

  if (!stats) return <div className="text-slate-500 text-sm">Loading dashboard…</div>;

  if (user?.role === "customer") {
    return (
      <div data-testid="customer-dashboard">
        <PageHeader
          eyebrow="Overview"
          title={`Welcome, ${user.name}`}
          description="Your account snapshot — orders, balances, and recent activity."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi testid="kpi-outstanding" label="Outstanding" value={fmtINR(stats.outstanding)} icon={AlertCircle} accent="rose" hint="Pending dues" />
          <Kpi testid="kpi-advance" label="Advance" value={fmtINR(stats.advance)} icon={PiggyBank} accent="green" hint="Credit balance" />
          <Kpi testid="kpi-month-purchase" label="This Month Purchase" value={fmtINR(stats.month_purchase)} icon={TrendingUp} accent="cobalt" />
          <Kpi testid="kpi-total-orders" label="Total Orders" value={stats.total_orders} icon={ShoppingCart} accent="blue" />
          <Kpi testid="kpi-last-payment" label="Last Payment" value={fmtINR(stats.last_payment)} hint={stats.last_payment_date || "—"} icon={IndianRupee} accent="slate" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <PageHeader
        eyebrow="Control Room"
        title="Distributor Dashboard"
        description="Real-time snapshot of today's revenue, outstanding receivables, and operating health."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <Kpi testid="kpi-todays-orders" label="Today's Orders" value={stats.todays_orders} icon={ShoppingCart} accent="cobalt" />
        <Kpi testid="kpi-todays-revenue" label="Today's Revenue" value={fmtINR(stats.todays_revenue)} icon={IndianRupee} accent="green" />
        <Kpi testid="kpi-outstanding" label="Outstanding" value={fmtINR(stats.outstanding)} icon={AlertCircle} accent="rose" />
        <Kpi testid="kpi-advance" label="Advance" value={fmtINR(stats.advance)} icon={PiggyBank} accent="amber" />
        <Kpi testid="kpi-customers" label="Customers" value={stats.total_customers} icon={Users} accent="blue" />
        <Kpi testid="kpi-products" label="Products" value={stats.total_products} icon={Package} accent="slate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Card className="lg:col-span-2" testid="chart-monthly">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold text-slate-900">Sales vs Collection — Last 6 months</h3>
            <span className="text-xs text-slate-500">In ₹</span>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <AreaChart data={stats.monthly_trend}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0047AB" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#0047AB" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} formatter={(v) => fmtINR(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="sales" name="Sales" stroke="#0047AB" fill="url(#g1)" strokeWidth={2} />
                <Area type="monotone" dataKey="collection" name="Collection" stroke="#10B981" fill="url(#g2)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card testid="chart-bars">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold text-slate-900">Outstanding Trend</h3>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={stats.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} formatter={(v) => fmtINR(v)} />
                <Bar dataKey="sales" fill="#0047AB" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
